import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Check, ArrowLeft, ArrowRight } from "lucide-react";
import { SiteHeader } from "../../components/site/Layout";
import { useApi, publicApi, errMsg } from "../../lib/api";
import { StepDiscovery, StepQuestions } from "./StepDiscovery";
import StepProfile from "./StepProfile";
import StepPackages from "./StepPackages";
import StepCustomise from "./StepCustomise";
import StepQuote from "./StepQuote";
import HelpWidget from "../../components/HelpWidget";
import AIChat from "../../components/AIChat";
import { Sheet, SheetContent, SheetTitle } from "../../components/ui/sheet";
import { Sparkles } from "lucide-react";

const STEPS = ["Your business", "Questions", "Confirm profile", "Packages", "Customise", "Details & quote"];

function initialStep(p) {
  if (p.accepted_quote_id && !p.quote_needs_revision) return 5;
  if (p.profile_confirmed) return (p.selection?.adds?.length || p.selection?.removes?.length) ? 4 : 3;
  return p.discovery_text ? 1 : 0;
}

// Back/Next: fixed bottom bar on phones & tablets (thumb reach), sticky bar under the stepper on desktop.
function StepNav({ step, last, canNext, go, finish }) {
  const back = (k) => <button onClick={() => go(step - 1)} disabled={step === 0} className="px-4 py-2 rounded-full border bg-white text-sm font-medium inline-flex items-center gap-1 disabled:opacity-40" data-testid={`stepnav-back-${k}`}><ArrowLeft className="w-4 h-4" />Back</button>;
  const next = (k) => (step === last && finish
    ? <button onClick={finish} className="btn-emerald !py-2" data-testid={`stepnav-next-${k}`}>Continue to my project<ArrowRight className="w-4 h-4" /></button>
    : <button onClick={() => go(step + 1)} disabled={!canNext} title={canNext ? "" : "Finish this step first"} className="btn-emerald !py-2 disabled:opacity-40" data-testid={`stepnav-next-${k}`}>{step === last ? "Last step" : `Next: ${STEPS[step + 1]}`}<ArrowRight className="w-4 h-4" /></button>);
  return (<>
    <div className="hidden lg:flex sticky top-20 z-30 mt-3 items-center justify-between rounded-full border bg-white/90 backdrop-blur px-2 py-1.5" data-testid="stepnav-desktop">{back("desktop")}<span className="text-xs text-slate-500">Step {step + 1} of {last + 1}: {STEPS[step]}</span>{next("desktop")}</div>
    <div className="lg:hidden fixed inset-x-0 bottom-0 z-40 border-t bg-white/95 backdrop-blur px-4 py-3 flex items-center justify-between gap-3" style={{ paddingBottom: "max(12px, env(safe-area-inset-bottom))" }} data-testid="stepnav-mobile">{back("mobile")}<span className="text-[11px] text-slate-500">{step + 1}/{last + 1}</span>{next("mobile")}</div>
  </>);
}

export default function Configurator() {
  const { pid } = useParams();
  const api = useApi();
  const nav = useNavigate();
  const [project, setProject] = useState(null);
  const [step, setStep] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [currency, setCurrency] = useState(null);
  const [chatOpen, setChatOpen] = useState(false);
  const [sync, setSync] = useState(0);

  useEffect(() => { publicApi.get("/public/config").then((r) => setCfg(r.data)); }, []);
  useEffect(() => { document.body.classList.add("has-stepnav"); return () => document.body.classList.remove("has-stepnav"); }, []);
  const started = useRef(false);
  useEffect(() => {
    if (pid || started.current) return;
    started.current = true;
    api.get("/projects").then(async (r) => {
      const open = r.data.find((p) => ["discovery", "requirements", "recommendations", "package", "customisation", "quotation", "details"].includes(p.stage));
      const id = open ? open.id : (await api.post("/projects")).data.id;
      nav(`/build/${id}`, { replace: true });
    }).catch((e) => toast.error(errMsg(e)));
  }, [pid, api, nav]);

  const reload = useCallback(async () => {
    const r = await api.get(`/projects/${pid}`);
    setProject(r.data.project);
    return r.data.project;
  }, [api, pid]);

  useEffect(() => {
    if (!pid) return;
    reload().then((p) => {
      const q = Number(new URLSearchParams(window.location.search).get("step"));
      setStep((s) => (s === null ? (q >= 1 && q <= 5 && p.profile_confirmed ? q : initialStep(p)) : s));
    }).catch((e) => toast.error(errMsg(e)));
  }, [pid, reload]);
  useEffect(() => { if (project && cfg && !currency) setCurrency(project.currency || cfg.suggested_currency || cfg.base_currency); }, [project, cfg, currency]);

  if (!project || step === null || !cfg || !currency) return <><SiteHeader /><div className="p-10 text-sm text-slate-500" data-testid="configurator-loading">Loading your project…</div></>;
  const maxStep = initialStep(project) + (project.profile_confirmed ? 2 : 0);
  const onChat = async () => { await reload(); setSync((s) => s + 1); };
  const props = { project, api, reload, go: setStep, currency, cfg, setCurrency, onChat, openChat: () => setChatOpen(true) };
  return (
    <div className="min-h-screen bg-[#faf8f2]">
      <SiteHeader />
      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-8">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div><p className="eyebrow">Website configurator</p><h1 className="text-3xl md:text-4xl font-semibold text-[#062a20]">{project.profile?.business_name || "Let's plan your website"}</h1></div>
          <div className="flex items-center gap-3">
            <label className="text-xs text-slate-500" htmlFor="currency-select">Show prices in</label>
            <select id="currency-select" value={currency} onChange={(e) => setCurrency(e.target.value)} className="h-9 rounded-md border bg-white px-2 text-sm" data-testid="currency-select">
              {cfg.currencies.map((c) => <option key={c.code} value={c.code}>{c.code} — {c.name}</option>)}
            </select>
            <Link to="/dashboard" className="text-xs underline text-slate-500" data-testid="configurator-dashboard-link">Saved automatically · Exit</Link>
          </div>
        </div>
        <ol className="mt-6 flex gap-1 overflow-x-auto pb-2" aria-label="Progress" data-testid="configurator-stepper">
          {STEPS.map((s, i) => {
            const reachable = i <= Math.max(maxStep, step);
            return (
              <li key={s} className="flex-1 min-w-[110px]">
                <button disabled={!reachable} onClick={() => setStep(i)} data-testid={`step-${i}`} aria-current={i === step ? "step" : undefined}
                  className={`w-full text-left border-t-2 pt-2 text-xs font-medium transition-colors ${i === step ? "border-[#b8892b] text-[#062a20]" : i < step ? "border-[#0b3d2e] text-[#0b3d2e]" : "border-slate-200 text-slate-400"}`}>
                  <span className="inline-flex items-center gap-1">{i < step && <Check className="w-3 h-3" />}{i + 1}. {s}</span>
                </button>
              </li>);
          })}
        </ol>
        <StepNav step={step} last={STEPS.length - 1} canNext={step + 1 <= Math.max(maxStep, step) && step < STEPS.length - 1} go={(n) => { setStep(n); window.scrollTo({ top: 0, behavior: "smooth" }); }}
          finish={project.accepted_quote_id ? () => nav(`/dashboard/${project.id}`) : null} />
        <div className="mt-4 lg:mt-8 fade-up pb-24 lg:pb-0" key={`${step}-${step === 0 ? 0 : sync}`}>
          {step === 0 && <StepDiscovery {...props} />}
          {step === 1 && <StepQuestions {...props} />}
          {step === 2 && <StepProfile {...props} />}
          {step === 3 && <StepPackages {...props} />}
          {step === 4 && <StepCustomise {...props} />}
          {step === 5 && <StepQuote {...props} />}
        </div>
      </div>
      {step !== 0 && <button onClick={() => setChatOpen(true)} className="fixed bottom-20 lg:bottom-6 left-4 lg:left-6 z-40 btn-emerald !rounded-full shadow-xl" data-testid="open-ai-chat"><Sparkles className="w-4 h-4" /> Ask AI</button>}
      <Sheet open={chatOpen} onOpenChange={setChatOpen}>
        <SheetContent side="right" className="w-full sm:max-w-lg p-0 flex flex-col" data-testid="ai-chat-sheet">
          <SheetTitle className="sr-only">Altiora AI assistant</SheetTitle>
          <AIChat project={project} api={api} onChange={onChat} className="flex-1 !rounded-none !border-0" />
        </SheetContent>
      </Sheet>
      <HelpWidget />
    </div>
  );
}
